/**
 * Admin Authentication Service
 * Handles admin login, logout, and token management
 */

import { api } from '@/lib/axios';
import { useAdminAuthStore } from '@/stores/adminAuthStore';
import type { AdminAuthResponse, AdminLoginRequest } from '@/types/admin';

export const adminAuthService = {
  /**
   * Login admin user
   */
  async login(email: string, password: string): Promise<AdminAuthResponse> {
    const requestData: AdminLoginRequest = {
      admin: {
        email,
        password,
      },
    };

    const response = await api.post<AdminAuthResponse>(
      '/v1/auth/sign_in',
      requestData
    );

    // Extract JWT token from Authorization header
    // Axios normalizes headers to lowercase
    const authHeader = response.headers['authorization'] || response.headers['Authorization'];
    const token = authHeader?.replace('Bearer ', '');

    if (!token) {
      throw new Error('No se recibió el token de autenticación');
    }

    // Store admin and token in store
    useAdminAuthStore.getState().setAdmin(response.data.admin, token);

    return response.data;
  },

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    try {
      await api.delete('/v1/auth/sign_out');
    } catch {
      // Continue with local logout even if API call fails
    } finally {
      // Clear auth state regardless of API response
      useAdminAuthStore.getState().clearAuth();
    }
  },

  /**
   * Get current admin from store
   */
  getCurrentAdmin() {
    return useAdminAuthStore.getState().admin;
  },

  /**
   * Check if admin is authenticated
   */
  isAuthenticated() {
    return useAdminAuthStore.getState().isAuthenticated;
  },

  /**
   * Get current token
   */
  getToken() {
    return useAdminAuthStore.getState().token;
  },
};
